import { api } from '../api.ts';
import { publicPage, wireChrome } from '../components/chrome.ts';
import { formData, html, mount, type Raw } from '../html.ts';
import { t } from '../i18n.ts';
import { brand } from '../state.ts';
import { errorMessage, field } from '../ui.ts';

const ERROR_BOX = html`<div class="p-alert p-alert--danger" role="alert" hidden data-error><span class="p-alert__icon" aria-hidden="true">!</span><div data-error-text></div></div>`;

function page(root: HTMLElement, body: Raw): void {
  mount(
    root,
    publicPage(html`<main class="pma-auth pma-auth--single">
      <section class="p-card pma-auth__card"><div class="p-card__body p-stack" data-p-gap="4">
        <img class="pma-brand-logo" src="${brand.logo}" alt="" width="40" height="40">
        ${body}
      </div></section>
    </main>`),
  );
  wireChrome(root);
}

async function submit(root: HTMLElement, form: HTMLFormElement, action: () => Promise<void>): Promise<void> {
  const button = form.querySelector<HTMLButtonElement>('button[type="submit"]')!;
  const errorBox = root.querySelector<HTMLElement>('[data-error]')!;
  button.disabled = true;
  errorBox.hidden = true;
  try {
    await action();
  } catch (error) {
    errorBox.querySelector('[data-error-text]')!.textContent = errorMessage(error);
    errorBox.hidden = false;
    button.disabled = false;
  }
}

/**
 * /reset asks for the email address; /reset#pmar_… (link from the mail) sets
 * the new password. The token lives in the fragment so it never reaches a
 * server log; it is removed from the address bar right away.
 */
export async function renderReset(root: HTMLElement, onDone: () => void): Promise<void> {
  const token = location.hash.match(/^#(pmar_[A-Za-z0-9_-]{20,100})$/)?.[1];
  if (token) {
    history.replaceState(null, '', '/reset');
    await renderNewPassword(root, token, onDone);
    return;
  }
  page(
    root,
    html`<h1>${t('Passwort vergessen')}</h1>
      <p>${t('Gib die E-Mail-Adresse deines Kontos ein. Du bekommst einen Link, mit dem du ein neues Passwort festlegst.')}</p>
      <form class="p-stack" data-p-gap="4" novalidate>
        ${field(t('E-Mail'), 'email', 'email', { required: true, attrs: 'autocomplete="email"' })}
        ${ERROR_BOX}
        <button class="p-button p-button--primary" type="submit">${t('Link senden')}</button>
      </form>
      <p class="pma-muted pma-small"><a href="/login">${t('Zurück zur Anmeldung')}</a></p>`,
  );
  const form = root.querySelector('form')!;
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (!form.reportValidity()) return;
    const email = formData(form).email as string;
    void submit(root, form, async () => {
      const result = await api.post<{ mail: boolean }>('/auth/password-reset', { email });
      page(
        root,
        result.mail
          ? html`<h1>${t('Schau in dein Postfach')}</h1>
              <p role="status">${t('Wenn es für {email} ein Konto gibt, ist ein Link unterwegs. Er ist eine Stunde gültig.', { email })}</p>
              <p class="pma-muted pma-small">${t('Nichts angekommen? Prüfe den Spam-Ordner oder fordere nach ein paar Minuten einen neuen Link an.')}</p>
              <p><a href="/login">${t('Zurück zur Anmeldung')}</a></p>`
          : html`<h1>${t('Zurücksetzen nicht verfügbar')}</h1>
              <p role="status">${t('Diese Installation verschickt keine E-Mails. Bitte wende dich an die Person, die PMA für dich betreibt.')}</p>
              <p><a href="/login">${t('Zurück zur Anmeldung')}</a></p>`,
      );
    });
  });
  (form.querySelector('input') as HTMLInputElement).focus();
}

async function renderNewPassword(root: HTMLElement, token: string, onDone: () => void): Promise<void> {
  let email: string;
  try {
    email = (await api.post<{ email: string }>('/auth/password-reset/check', { token })).email;
  } catch (error) {
    page(
      root,
      html`<h1>${t('Link nicht gültig')}</h1>
        <p>${t('Der Link ist abgelaufen oder wurde schon verwendet. Fordere einen neuen an.')}</p>
        <p class="pma-muted pma-small">${errorMessage(error)}</p>
        <p><a class="p-button p-button--primary" href="/reset">${t('Neuen Link anfordern')}</a></p>`,
    );
    return;
  }
  page(
    root,
    html`<h1>${t('Neues Passwort festlegen')}</h1>
      <p>${t('Für {email}. Danach bist du auf allen anderen Geräten abgemeldet.', { email })}</p>
      <form class="p-stack" data-p-gap="4" novalidate>
        <input type="email" name="username" value="${email}" autocomplete="username" hidden>
        ${field(t('Neues Passwort'), 'password', 'password', { required: true, hint: t('Mindestens 10 Zeichen'), attrs: 'minlength="10" autocomplete="new-password"' })}
        ${field(t('Passwort wiederholen'), 'repeat', 'password', { required: true, attrs: 'minlength="10" autocomplete="new-password"' })}
        ${ERROR_BOX}
        <button class="p-button p-button--primary" type="submit">${t('Passwort speichern')}</button>
      </form>`,
  );
  const form = root.querySelector('form')!;
  const repeat = form.querySelector<HTMLInputElement>('[name="repeat"]')!;
  repeat.addEventListener('input', () => repeat.setCustomValidity(''));
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const data = formData(form);
    repeat.setCustomValidity(data.password === data.repeat ? '' : t('Die Passwörter stimmen nicht überein.'));
    if (!form.reportValidity()) return;
    void submit(root, form, async () => {
      await api.post('/auth/password-reset/complete', { token, password: data.password });
      onDone();
    });
  });
  form.querySelector<HTMLInputElement>('[name="password"]')!.focus();
}
